import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

type ModuleFactory<T extends ComponentType<unknown>> = () => Promise<{
  default: T;
}>;

export type LazyRetryCallbacks = {
  onRetry?: (info: {
    attempt: number;
    error: unknown;
    delayMs: number;
  }) => void;
  onFinalFailure?: (info: { attempts: number; error: unknown }) => void;
  onSuccessAfterRetry?: (info: { attempts: number }) => void;
};

export type LazyWithRetryOptions = LazyRetryCallbacks & {
  /**
   * Maximum number of retries after the initial attempt. The total number of
   * attempts is `maxRetries + 1`.
   * @default 5
   */
  maxRetries?: number;
  /**
   * Exponential backoff base. The first retry runs immediately; each
   * subsequent retry waits `baseDelayMs * 2^(retryIndex - 1)` ms. With defaults
   * the retry delay sequence is: 0, 200, 400, 800, 1600 ms.
   * @default 200
   */
  baseDelayMs?: number;
  /**
   * Injectable sleep primitive. Tests can pass a synchronous spy to avoid
   * real timers.
   */
  sleep?: (ms: number) => Promise<void>;
};

const defaultSleep = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Wraps `React.lazy` with a retry loop for dynamic imports. Retries are
 * intended to mitigate transient failures: intermittent network, constrained
 * devices, or stale deployments that are being rolled forward.
 *
 * The UI library stays free of logging concerns; callers can subscribe to
 * `onRetry`, `onSuccessAfterRetry`, and `onFinalFailure` to emit telemetry.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const lazyWithRetry = <T extends ComponentType<any>>(
  factory: ModuleFactory<T>,
  options: LazyWithRetryOptions = {},
): LazyExoticComponent<T> => {
  const {
    maxRetries = 5,
    baseDelayMs = 200,
    sleep = defaultSleep,
    onRetry,
    onSuccessAfterRetry,
    onFinalFailure,
  } = options;

  const maxAttempts = maxRetries + 1;

  // Delay to wait BEFORE the given 1-indexed attempt. Attempts 1 and 2 run
  // immediately (initial load + first retry); each later attempt doubles.
  const getDelayBeforeAttempt = (nextAttempt: number): number => {
    if (nextAttempt <= 2) {
      return 0;
    }
    return baseDelayMs * 2 ** (nextAttempt - 3);
  };

  return lazy(async () => {
    let lastError: unknown;
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        const loaded = await factory();
        if (attempt > 1) {
          onSuccessAfterRetry?.({ attempts: attempt });
        }
        return loaded;
      } catch (error) {
        lastError = error;
        const nextAttempt = attempt + 1;
        if (nextAttempt > maxAttempts) {
          break;
        }
        const delayMs = getDelayBeforeAttempt(nextAttempt);
        onRetry?.({ attempt, error, delayMs });
        if (delayMs > 0) {
          await sleep(delayMs);
        }
      }
    }

    onFinalFailure?.({ attempts: maxAttempts, error: lastError });
    throw lastError;
  });
};
